/**
 * SeKa Cart Drawer - AJAX Mini-Warenkorb
 * Handles add-to-cart, quantity changes, and cart updates without page reload
 */

(function() {
  'use strict';

  const CartDrawer = {
    drawer: null,
    body: null,
    footer: null,
    countElements: null,
    subtotalElement: null,
    isOpen: false,
    isLoading: false,

    /**
     * Initialize cart drawer
     */
    init: function() {
      this.drawer = document.getElementById('cart-drawer');
      if (!this.drawer) return;

      this.body = this.drawer.querySelector('[data-cart-drawer-body]');
      this.footer = this.drawer.querySelector('[data-cart-footer]');
      this.countElements = document.querySelectorAll('[data-cart-count]');
      this.subtotalElement = this.drawer.querySelector('[data-cart-subtotal]');

      this.bindEvents();
      this.bindProductForms();
      console.log('Cart Drawer initialized');
    },

    /**
     * Bind drawer events
     */
    bindEvents: function() {
      const self = this;

      // Close buttons
      document.querySelectorAll('[data-cart-drawer-close]').forEach(function(btn) {
        btn.addEventListener('click', function(e) {
          if (e.target.closest('.cart-drawer__continue')) {
            // Don't close on "continue shopping" link, let it navigate
          }
          self.close();
        });
      });

      // Open buttons (header cart icon)
      document.querySelectorAll('[data-cart-drawer-open]').forEach(function(btn) {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          self.open();
        });
      });

      // Close on escape key
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && self.isOpen) {
          self.close();
        }
      });

      // Quantity buttons and remove in drawer
      this.drawer.addEventListener('click', function(e) {
        const target = e.target.closest('[data-action]');
        if (!target) return;

        const action = target.getAttribute('data-action');
        const lineKey = target.getAttribute('data-line-key');

        if (action === 'decrease') {
          self.updateQuantity(lineKey, -1);
        } else if (action === 'increase') {
          self.updateQuantity(lineKey, 1);
        } else if (action === 'remove') {
          self.removeItem(lineKey);
        }
      });

      // Quantity input change
      this.drawer.addEventListener('change', function(e) {
        if (e.target.matches('.cart-drawer-item__qty-input')) {
          const lineKey = e.target.getAttribute('data-line-key');
          const quantity = parseInt(e.target.value, 10);
          if (quantity > 0) {
            self.setQuantity(lineKey, quantity);
          }
        }
      });
    },

    /**
     * Bind product forms for AJAX add-to-cart
     */
    bindProductForms: function() {
      const self = this;

      document.querySelectorAll('form[action*="/cart/add"]').forEach(function(form) {
        if (form.hasAttribute('data-ajax-bound')) return;
        form.setAttribute('data-ajax-bound', 'true');

        form.addEventListener('submit', function(e) {
          e.preventDefault();
          self.addToCart(form);
        });
      });
    },

    /**
     * Open cart drawer
     */
    open: function() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      
      // GTM: view_cart Event
      var self = this;
      fetch('/cart.js')
        .then(function(response) { return response.json(); })
        .then(function(cart) {
          if (window.SeKaDataLayer && cart.items.length > 0) {
            window.SeKaDataLayer.viewCart(cart);
          }
        });
    },

    /**
     * Close cart drawer
     */
    close: function() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    },

    /**
     * Set loading state
     */
    setLoading: function(loading) {
      this.isLoading = loading;
      this.drawer.classList.toggle('is-loading', loading);
    },

    /**
     * Add item to cart via AJAX
     */
    addToCart: function(form) {
      const self = this;
      const formData = new FormData(form);
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : '';

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Wird hinzugefügt...';
      }

      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        body: formData
      })
      .then(function(response) {
        return response.json();
      })
      .then(function(item) {
        if (item.status) {
          // Error from Shopify
          self.showToast(item.description || 'Fehler beim Hinzufügen', 'error');
          return;
        }

        // GTM: add_to_cart Event
        if (window.SeKaDataLayer) {
          window.SeKaDataLayer.addToCart(item, parseInt(formData.get('quantity') || 1));
        }

        self.showToast(item.product_title + ' wurde hinzugefügt', 'success');
        self.refreshCart();
        self.open();
      })
      .catch(function(error) {
        console.error('Add to cart error:', error);
        self.showToast('Fehler beim Hinzufügen', 'error');
      })
      .finally(function() {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
      });
    },

    /**
     * Update item quantity (increment/decrement)
     */
    updateQuantity: function(lineKey, change) {
      const item = this.drawer.querySelector('[data-line-key="' + lineKey + '"]');
      if (!item) return;

      const input = item.querySelector('.cart-drawer-item__qty-input');
      const currentQty = parseInt(input.value, 10) || 1;
      const newQty = Math.max(0, currentQty + change);

      if (newQty === 0) {
        this.removeItem(lineKey);
      } else {
        this.setQuantity(lineKey, newQty);
      }
    },

    /**
     * Set item to specific quantity
     */
    setQuantity: function(lineKey, quantity) {
      const self = this;
      const item = this.drawer.querySelector('[data-line-key="' + lineKey + '"]');
      
      if (item) {
        item.classList.add('is-updating');
      }
      this.setLoading(true);

      const formData = new FormData();
      formData.append('id', lineKey);
      formData.append('quantity', quantity);

      fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        body: formData
      })
      .then(function(response) {
        return response.json();
      })
      .then(function(cart) {
        self.refreshCart();
      })
      .catch(function(error) {
        console.error('Update quantity error:', error);
        self.showToast('Fehler beim Aktualisieren', 'error');
        self.setLoading(false);
        if (item) item.classList.remove('is-updating');
      });
    },

    /**
     * Remove item from cart
     */
    removeItem: function(lineKey) {
      var self = this;
      var item = this.drawer.querySelector('[data-line-key="' + lineKey + '"]');
      
      // GTM: remove_from_cart Event - get item data before removing
      if (window.SeKaDataLayer && item) {
        var itemData = {
          product_title: item.querySelector('.cart-drawer-item__title')?.textContent?.trim() || '',
          variant_title: item.querySelector('.cart-drawer-item__variant')?.textContent?.trim() || '',
          price: parseInt(item.querySelector('[data-price-gross]')?.dataset?.priceGross || 0),
          quantity: parseInt(item.querySelector('.cart-drawer-item__qty-input')?.value || 1),
          variant_id: lineKey
        };
        window.SeKaDataLayer.removeFromCart(itemData, itemData.quantity);
      }
      
      this.setQuantity(lineKey, 0);
    },

    /**
     * Refresh cart contents via AJAX
     */
    refreshCart: function() {
      const self = this;
      this.setLoading(true);

      fetch('/cart.js')
      .then(function(response) {
        return response.json();
      })
      .then(function(cart) {
        self.updateCartCount(cart.item_count);
        self.updateDrawerContents(cart);
        self.setLoading(false);
      })
      .catch(function(error) {
        console.error('Refresh cart error:', error);
        self.setLoading(false);
      });
    },

    /**
     * Update cart count badges
     */
    updateCartCount: function(count) {
      this.countElements = document.querySelectorAll('[data-cart-count]');
      this.countElements.forEach(function(el) {
        el.textContent = count;
      });

      // Update header cart count
      const headerCount = document.querySelector('.header__cart-count');
      if (headerCount) {
        headerCount.textContent = count;
        headerCount.style.display = count > 0 ? '' : 'none';
      }
    },

    /**
     * Update drawer contents with new cart data
     */
    updateDrawerContents: function(cart) {
      const self = this;

      // Show empty state if no items
      if (cart.item_count === 0) {
        this.body.innerHTML = `
          <div class="cart-drawer__empty">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/>
              <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
            </svg>
            <p>Ihr Warenkorb ist leer</p>
            <a href="${window.routes?.all_products_collection_url || '/collections/all'}" class="cart-drawer__continue" data-cart-drawer-close>
              Weiter einkaufen
            </a>
          </div>
        `;
        // Update subtotal and hide footer
        this.updateSubtotal(cart);
        if (this.footer) {
          this.footer.style.display = 'none';
        }
        return;
      }

      // Cart has items - fetch rendered HTML for cart items
      fetch('/?sections=cart-drawer-items')
      .then(function(response) {
        return response.json();
      })
      .then(function(sections) {
        if (sections['cart-drawer-items']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['cart-drawer-items'], 'text/html');
          const newItems = doc.querySelector('[data-cart-items]');
          
          if (newItems) {
            // Check if items container exists, if not create the structure
            let itemsContainer = self.drawer.querySelector('[data-cart-items]');
            if (!itemsContainer) {
              // Replace empty state with items container
              self.body.innerHTML = '<div class="cart-drawer__items" data-cart-items></div>';
              itemsContainer = self.drawer.querySelector('[data-cart-items]');
            }
            if (itemsContainer) {
              itemsContainer.innerHTML = newItems.innerHTML;
            }
          }
        }
      })
      .catch(function(error) {
        // Fallback: reload page if section rendering fails
        console.log('Section rendering not available, using fallback', error);
      });

      // Update subtotal
      this.updateSubtotal(cart);

      // Show footer when cart has items
      if (this.footer) {
        this.footer.style.display = '';
      }
    },

    /**
     * Update subtotal display
     */
    updateSubtotal: function(cart) {
      if (!this.subtotalElement) return;

      const vatRate = window.SeKaTheme?.vatRate || 19;
      const vatMultiplier = 100 + vatRate;
      const taxesIncluded = window.SeKaTheme?.taxesIncluded ?? true;

      let grossPrice, netPrice;
      if (taxesIncluded) {
        grossPrice = cart.total_price;
        netPrice = Math.round(cart.total_price * 100 / vatMultiplier);
      } else {
        netPrice = cart.total_price;
        grossPrice = Math.round(cart.total_price * vatMultiplier / 100);
      }

      const formatMoney = window.SeKaVat?.formatMoney || function(price) {
        return (price / 100).toFixed(2).replace('.', ',') + ' €';
      };

      this.subtotalElement.innerHTML = `
        <span class="price-display__gross" data-price-gross="${grossPrice}">${formatMoney(grossPrice)}</span>
        <span class="price-display__net" data-price-net="${netPrice}">${formatMoney(netPrice)}</span>
      `;
    },

    /**
     * Show toast notification
     */
    showToast: function(message, type) {
      type = type || 'success';

      // Remove existing toast
      const existingToast = document.querySelector('.cart-toast');
      if (existingToast) {
        existingToast.remove();
      }

      // Create new toast
      const toast = document.createElement('div');
      toast.className = 'cart-toast is-' + type;
      toast.textContent = message;
      document.body.appendChild(toast);

      // Show toast
      setTimeout(function() {
        toast.classList.add('is-visible');
      }, 10);

      // Hide and remove toast
      setTimeout(function() {
        toast.classList.remove('is-visible');
        setTimeout(function() {
          toast.remove();
        }, 300);
      }, 3000);
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      CartDrawer.init();
    });
  } else {
    CartDrawer.init();
  }

  // Re-bind forms after AJAX navigation (for SPAs)
  document.addEventListener('shopify:section:load', function() {
    CartDrawer.bindProductForms();
  });

  // Expose globally
  window.SeKaCartDrawer = CartDrawer;
})();
